import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import SignInPage from './pages/SignInPage';
import AuthHandoffPage from './pages/AuthHandoffPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ValiderFacturesPage from './pages/ValiderFacturesPage';
import DashboardPage from './pages/DashboardPage';
import LogsPage from './pages/LogsPage';
import ErrorLogDetailsPage from './pages/ErrorLogDetailsPage';
import PendingInvoicesPage from './pages/PendingInvoicesPage';
import DropInvoicesPage from './pages/DropInvoicesPage';
import ToAssignPage from './pages/ToAssignPage';
import InvoicesPage from './pages/InvoicesPage';
import EditInvoicePage from './pages/EditInvoicePage';
import MyProvidersPage from './pages/MyProvidersPage';
import AssignLogPage from './pages/AssignLogPage';
import ColocDetailsPage from './pages/ColocDetailsPage';
import ChargeCalculationsPage from './pages/ChargeCalculationsPage';
import ChargeCalculationDetailPage from './pages/ChargeCalculationDetailPage';
import UsersPage from './pages/UsersPage';
import MyAccountPage from './pages/MyAccountPage';
import TenantsPage from './pages/TenantsPage';
import TenantDetailPage from './pages/TenantDetailPage';
import ApercuEspaceLocatairePage from './pages/ApercuEspaceLocatairePage';
import TenantDashboardPage from './pages/TenantDashboardPage';
import TenantDossierPage from './pages/TenantDossierPage';
import RoomsPage from './pages/RoomsPage';
import RoomDetailPage from './pages/RoomDetailPage';
import StatistiquesPage from './pages/StatistiquesPage';
import CandidatsPage from './pages/CandidatsPage';
import CandidatDetailPage from './pages/CandidatDetailPage';
import ContactsPage from './pages/ContactsPage';
import ContactDetailPage from './pages/ContactDetailPage';
import AgencesPage from './pages/AgencesPage';
import AgenceDetailPage from './pages/AgenceDetailPage';
import AgencyContactsPage from './pages/AgencyContactsPage';
import AgencyContactDetailPage from './pages/AgencyContactDetailPage';
import AgencyTenantDocumentsPage from './pages/AgencyTenantDocumentsPage';
import AgencyDocumentDropPage from './pages/AgencyDocumentDropPage';
import SettingsPage from './pages/SettingsPage';
import CreateColocPage from './pages/CreateColocPage';
import OnboardingPage from './pages/OnboardingPage';
import Layout from './components/Layout';
import TenantLayout from './components/TenantLayout';
import OwnerRoute from './components/OwnerRoute';
import OnboardingGate from './components/OnboardingGate';
import AccessGate from './components/AccessGate';
import CapabilityGate, { UpgradeScreen } from './components/CapabilityGate';
import { CapabilitiesProvider } from './contexts/CapabilitiesContext';
import TenantRoute from './components/TenantRoute';
import AgencyRoute from './components/AgencyRoute';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import './App.css';

/** Échap ferme la fenêtre ouverte au premier plan (ticket #25). Chaque fenêtre
 *  se ferme déjà d'un clic sur son fond : on rejoue ce clic, plutôt que
 *  d'ajouter un écouteur dans chacune des vingt-cinq. */
const fermerAuClavier = (e: KeyboardEvent) => {
  if (e.key !== 'Escape') return;
  const fonds = document.querySelectorAll<HTMLElement>(
    '.modal-overlay, .photo-modal-backdrop, .subscription-modal-backdrop',
  );
  fonds[fonds.length - 1]?.click();
};

function App() {
  useEffect(() => {
    document.addEventListener('keydown', fermerAuClavier);
    return () => document.removeEventListener('keydown', fermerAuClavier);
  }, []);

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/auth/handoff" element={<AuthHandoffPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Publique : le lien de vérification est ouvert depuis un client
            mail, souvent hors de la session du back-office. */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* Publique : valider ses factures depuis l'email récapitulatif, sans
            se connecter — le lien porte l'autorisation. */}
        <Route path="/factures/valider" element={<ValiderFacturesPage />} />

        {/* Owner routes */}
        <Route element={<OwnerRoute />}>
          {/* Garde d'accès/abonnement : un compte expiré ne va ni en onboarding ni dans l'app */}
          <Route element={<AccessGate />}>
          {/* Catalogue de capacités (saas/12) : chargé une fois, servi par l'API */}
          <Route element={<CapabilitiesProvider />}>
          {/* Onboarding : owner connecté, hors du gate onboarding (sinon boucle) */}
          <Route path="/onboarding" element={<OnboardingPage />} />

          {/* Toutes les autres routes owner passent par le gate d'onboarding */}
          <Route element={<OnboardingGate />}>
          <Route path="/dashboard" element={
            <Layout>
              <DashboardPage />
            </Layout>
          } />
          {/* Hors offre (capacités « unavailable ») : masqués via CapabilityGate */}
          <Route path="/logs" element={
            <Layout>
              <CapabilityGate capability="logs"><LogsPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/pending-invoices" element={
            <Layout>
              <CapabilityGate capability="invoices"><PendingInvoicesPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/drop-invoices" element={
            <Layout>
              <CapabilityGate capability="invoices"><DropInvoicesPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/pending-invoices/error/:id" element={
            <Layout>
              <CapabilityGate capability="invoices"><ErrorLogDetailsPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/to-assign" element={
            <Layout>
              <CapabilityGate capability="invoices"><ToAssignPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/to-assign/:id" element={
            <Layout>
              <CapabilityGate capability="invoices"><AssignLogPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/logs/error/:id" element={
            <Layout>
              <CapabilityGate capability="logs"><ErrorLogDetailsPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/logs/assign/:id" element={
            <Layout>
              <CapabilityGate capability="logs"><AssignLogPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/invoices" element={
            <Layout>
              <CapabilityGate capability="invoices"><InvoicesPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/invoices/edit/:id" element={
            <Layout>
              <CapabilityGate capability="invoices"><EditInvoicePage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/my-providers" element={
            <Layout>
              <CapabilityGate capability="providers"><MyProvidersPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/providers" element={<Navigate to="/my-providers" replace />} />
          <Route path="/providers/*" element={<Navigate to="/my-providers" replace />} />
          <Route path="/charges" element={
            <Layout>
              <CapabilityGate capability="charges"><ChargeCalculationsPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/charges/:id" element={
            <Layout>
              <CapabilityGate capability="charges"><ChargeCalculationDetailPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/details" element={
            <Layout>
              <ColocDetailsPage />
            </Layout>
          } />
          <Route path="/tenants" element={
            <Layout>
              <TenantsPage />
            </Layout>
          } />
          <Route path="/tenants/:id" element={
            <Layout>
              <TenantDetailPage />
            </Layout>
          } />
          {/* Sans <Layout> : l'aperçu prend l'habillage de l'espace locataire. */}
          <Route path="/tenants/:id/espace/:page" element={<ApercuEspaceLocatairePage />} />
          {/* Audience : incluse dans tous les paliers, donc pas de
              CapabilityGate — décision produit du 2026-08-23. */}
          <Route path="/statistiques" element={
            <Layout>
              <StatistiquesPage />
            </Layout>
          } />
          <Route path="/rooms" element={
            <Layout>
              <RoomsPage />
            </Layout>
          } />
          <Route path="/rooms/:id" element={
            <Layout>
              <RoomDetailPage />
            </Layout>
          } />
          {/* Périmètre payant (capacités « unlockable » sans carte) : écran
              verrouillé avec l'appel à l'action Stripe */}
          <Route path="/candidats" element={
            <Layout>
              <CapabilityGate capability="candidates"><CandidatsPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/candidats/:id" element={
            <Layout>
              <CapabilityGate capability="candidates"><CandidatDetailPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/contacts" element={
            <Layout>
              <CapabilityGate capability="contacts"><ContactsPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/contacts/:id" element={
            <Layout>
              <CapabilityGate capability="contacts"><ContactDetailPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/users" element={
            <Layout>
              <CapabilityGate capability="users"><UsersPage /></CapabilityGate>
            </Layout>
          } />
          {/* Même porte que le cadenas du menu : sans l'offre, l'écran d'offre
              plutôt qu'une page qui s'ouvre sur une erreur (ticket #11). */}
          <Route path="/agences" element={
            <Layout>
              <CapabilityGate capability="agency_access"><AgencesPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/agences/:id" element={
            <Layout>
              <CapabilityGate capability="agency_access"><AgenceDetailPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/settings" element={
            <Layout>
              <CapabilityGate capability="settings"><SettingsPage /></CapabilityGate>
            </Layout>
          } />
          <Route path="/upgrade" element={
            <Layout>
              <UpgradeScreen title="Passez au niveau supérieur : publiez votre site" />
            </Layout>
          } />
          <Route path="/my-account" element={
            <Layout>
              <MyAccountPage />
            </Layout>
          } />
          <Route path="/create-coloc" element={
            <Layout>
              <CreateColocPage />
            </Layout>
          } />
          </Route>{/* fin OnboardingGate */}
          </Route>{/* fin CapabilitiesProvider */}
          </Route>{/* fin AccessGate */}
        </Route>

        {/* Tenant routes */}
        <Route element={<TenantRoute />}>
          <Route path="/tenant/dashboard" element={
            <TenantLayout>
              <TenantDashboardPage />
            </TenantLayout>
          } />
          <Route path="/tenant/dossier" element={
            <TenantLayout>
              <TenantDossierPage />
            </TenantLayout>
          } />
        </Route>

        {/* Agency routes : lecture seule des candidatures des colocs partagées */}
        <Route element={<AgencyRoute />}>
          <Route path="/agency/candidats" element={
            <Layout>
              <CandidatsPage />
            </Layout>
          } />
          <Route path="/agency/candidats/:id" element={
            <Layout>
              <CandidatDetailPage />
            </Layout>
          } />
          <Route path="/agency/contacts" element={
            <Layout>
              <AgencyContactsPage />
            </Layout>
          } />
          <Route path="/agency/contacts/:id" element={
            <Layout>
              <AgencyContactDetailPage />
            </Layout>
          } />
          <Route path="/agency/documents" element={
            <Layout>
              <AgencyTenantDocumentsPage />
            </Layout>
          } />
          <Route path="/agency/documents/:roomId" element={
            <Layout>
              <AgencyDocumentDropPage />
            </Layout>
          } />
        </Route>

        {/* Root redirect based on role */}
        <Route path="/" element={<RoleBasedRedirect />} />
        <Route path="*" element={<RoleBasedRedirect />} />
      </Routes>
    </Router>
  );
}

export default App;
